/**
 * Color Scheme Presets
 * 
 * Predefined color schemes that users can choose from.
 * Each scheme works in both light and dark modes.
 */

export interface ColorScheme {
  id: string;
  name: string;
  description: string;
  colors: {
    primary: string;
    secondary: string;
    accent?: string;
  };
}

export const colorSchemes: ColorScheme[] = [
  {
    id: 'default',
    name: 'Ocean Blue',
    description: 'Professional blue and purple theme (default)',
    colors: {
      primary: '#2E86AB',
      secondary: '#A23B72',
    },
  },
  {
    id: 'forest',
    name: 'Forest Green',
    description: 'Natural green and earth tones',
    colors: {
      primary: '#2D6A4F',
      secondary: '#52B788',
      accent: '#74C69D',
    },
  },
  {
    id: 'sunset',
    name: 'Sunset Orange',
    description: 'Warm orange and coral colors',
    colors: {
      primary: '#E76F51',
      secondary: '#F4A261',
      accent: '#E9C46A',
    },
  },
  {
    id: 'twilight',
    name: 'Twilight Purple',
    description: 'Deep purple and violet theme',
    colors: {
      primary: '#6A4C93',
      secondary: '#9B5DE5',
      accent: '#C77DFF',
    },
  },
  {
    id: 'crimson',
    name: 'Crimson Red',
    description: 'Bold red and burgundy theme',
    colors: {
      primary: '#C1121F',
      secondary: '#780000',
      accent: '#FDF0D5',
    },
  },
  {
    id: 'slate',
    name: 'Slate Gray',
    description: 'Neutral gray and blue-gray theme',
    colors: {
      primary: '#475569',
      secondary: '#64748B',
      accent: '#94A3B8',
    },
  },
  {
    id: 'teal',
    name: 'Teal Cyan',
    description: 'Fresh teal and cyan colors',
    colors: {
      primary: '#0891B2',
      secondary: '#06B6D4',
      accent: '#22D3EE',
    },
  },
  {
    id: 'amber',
    name: 'Amber Gold',
    description: 'Warm amber and golden tones',
    colors: {
      primary: '#D97706',
      secondary: '#F59E0B',
      accent: '#FBBF24',
    },
  },
  {
    id: 'indigo',
    name: 'Indigo Night',
    description: 'Deep indigo and blue theme',
    colors: {
      primary: '#4338CA',
      secondary: '#6366F1',
      accent: '#818CF8',
    },
  },
  {
    id: 'rose',
    name: 'Rose Pink',
    description: 'Elegant rose and pink theme',
    colors: {
      primary: '#E11D48',
      secondary: '#FB7185',
      accent: '#FDA4AF',
    },
  },
  {
    id: 'navy',
    name: 'Navy Blue',
    description: 'Classic navy and steel blue',
    colors: {
      primary: '#1E3A8A',
      secondary: '#3B82F6',
      accent: '#60A5FA',
    },
  },
  {
    id: 'emerald',
    name: 'Emerald Green',
    description: 'Vibrant emerald and mint tones',
    colors: {
      primary: '#059669',
      secondary: '#10B981',
      accent: '#34D399',
    },
  },
  {
    id: 'magenta',
    name: 'Magenta Pink',
    description: 'Bold magenta and fuchsia theme',
    colors: {
      primary: '#C026D3',
      secondary: '#D946EF',
      accent: '#E879F9',
    },
  },
  {
    id: 'bronze',
    name: 'Bronze Copper',
    description: 'Rich bronze and copper shades',
    colors: {
      primary: '#92400E',
      secondary: '#B45309',
      accent: '#D97706',
    },
  },
  {
    id: 'lime',
    name: 'Lime Green',
    description: 'Fresh lime and chartreuse',
    colors: {
      primary: '#65A30D',
      secondary: '#84CC16',
      accent: '#A3E635',
    },
  },
  {
    id: 'violet',
    name: 'Royal Violet',
    description: 'Regal violet and lavender',
    colors: {
      primary: '#7C3AED',
      secondary: '#8B5CF6',
      accent: '#A78BFA',
    },
  },
];

/**
 * Get a color scheme by ID (supports both preset and custom themes)
 */
export const getColorScheme = (id: string): ColorScheme => {
  // Check if it's a custom theme
  if (id.startsWith('custom_')) {
    // Load from localStorage
    try {
      const customThemes = JSON.parse(localStorage.getItem('custom_color_themes') || '[]');
      const themeId = parseInt(id.replace('custom_', ''));
      const customTheme = customThemes.find((t: any) => t.id === themeId);
      
      if (customTheme) {
        return {
          id,
          name: customTheme.scheme_name,
          description: customTheme.description || 'Custom theme',
          colors: {
            primary: customTheme.primary_color,
            secondary: customTheme.secondary_color,
            accent: customTheme.accent_color,
          },
        };
      }
    } catch (error) {
      console.error('Failed to load custom theme:', error);
    }
  }
  
  // Return preset theme or default
  return colorSchemes.find(scheme => scheme.id === id) || colorSchemes[0];
};

/**
 * Helper function to lighten a color (for light mode variations)
 */
export const lightenColor = (hex: string, percent: number): string => {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return '#' + (
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  ).toString(16).slice(1).toUpperCase();
};

/**
 * Helper function to darken a color (for dark mode variations)
 */
export const darkenColor = (hex: string, percent: number): string => {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) - amt;
  const G = (num >> 8 & 0x00FF) - amt;
  const B = (num & 0x0000FF) - amt;
  return '#' + (
    0x1000000 +
    (R > 0 ? R : 0) * 0x10000 +
    (G > 0 ? G : 0) * 0x100 +
    (B > 0 ? B : 0)
  ).toString(16).slice(1).toUpperCase();
};

